import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Empty,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography
} from "antd";
import {
  ClearOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  SearchOutlined
} from "@ant-design/icons";
import { ToastContainer, toast } from "react-toastify";
import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import axios from "axios";
import { getHeaders } from "../../utility/getHeader";
import fetchData from "../../functions/fetchData";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import "react-toastify/dist/ReactToastify.css";
import "../master/ExpenseHead.css";
import "./StockReportNew.css";
import logo from "../../assets/logo.png";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const REPORT_TYPE_OPTIONS = [
  { value: "all", label: "All Records" },
  { value: "item", label: "By Item" },
  { value: "supplier", label: "By Supplier" },
  { value: "category", label: "By Category" },
  { value: "reference", label: "By Reference" },
  { value: "groupbyinv", label: "Group by Inv" },
  { value: "transactions", label: "Transactions" },
  { value: "balance", label: "Balance" }
];

// Report types whose rows carry per-item category/qty/sold_qty - eligible for the
// cross-cutting category filter and the "non-moving items" derivation
const ROW_LEVEL_REPORT_TYPES = ["all", "item", "supplier", "category", "transactions"];

const MOVEMENT_FILTER_COLUMNS = [
  { key: "name", label: "Item Name" },
  { key: "category", label: "Category" },
  { key: "sname", label: "Supplier" },
  { key: "total_inward", label: "Total In Qty", isNumeric: true },
  { key: "total_outward", label: "Total Out Qty", isNumeric: true },
  { key: "closing_stock_qty", label: "Overall Closing Stock Qty", isNumeric: true },
  { key: "rate", label: "Rate", isCurrency: true },
  { key: "total_amount", label: "Total Value", isCurrency: true },
  { key: "date", label: "Last Movement", isDate: true }
];

// Shown only when both movement checkboxes are active, so a combined report
// still tells you which condition(s) each row actually matched
const MOVEMENT_STATUS_COLUMN = { key: "movement_status", label: "Matched On" };

const getNumericValue = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === "") {
      continue;
    }

    const parsedValue = Number(value);
    if (Number.isFinite(parsedValue)) {
      return parsedValue;
    }
  }

  return null;
};

const getTextValue = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) {
      continue;
    }

    const trimmedValue = String(value).trim();
    if (trimmedValue) {
      return trimmedValue;
    }
  }

  return "";
};

// Compares [timestamp, po, id] tuples to find the most recent wstock row for an item
const isRankNewer = (candidate, current) => {
  for (let i = 0; i < candidate.length; i += 1) {
    if (candidate[i] !== current[i]) {
      return candidate[i] > current[i];
    }
  }
  return false;
};

export default function StockReportNew() {
  const [filterOptions, setFilterOptions] = useState({
    categories: [],
    items: [],
    suppliers: [],
    sponsors: [],
    references: [],
    date_range: { earliest_date: null, latest_date: null },
    stock_types: []
  });

  const [filters, setFilters] = useState({
    from: "",
    to: "",
    itemid: "",
    name: "",
    catid: "",
    sname: "",
    sponsor_name: "",
    ref: "",
    page: 1,
    limit: 50
  });

  const [reportData, setReportData] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    limit: 50,
    total_records: 0,
    total_pages: 0
  });
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [dateRange, setDateRange] = useState(null);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [showNonMovingOnly, setShowNonMovingOnly] = useState(false);
  const [showNoStockInOnly, setShowNoStockInOnly] = useState(false);

  const displayData = useMemo(() => {
    if (filterType !== "item") {
      return reportData;
    }

    const sortedRows = [...reportData].sort((left, right) => {
      const leftDate = new Date(left.date || 0).getTime();
      const rightDate = new Date(right.date || 0).getTime();

      if (leftDate !== rightDate) {
        return leftDate - rightDate;
      }

      const leftPo = Number(left.po) || 0;
      const rightPo = Number(right.po) || 0;

      if (leftPo !== rightPo) {
        return leftPo - rightPo;
      }

      return (Number(left.id) || 0) - (Number(right.id) || 0);
    });

    // Tracked per item name - the "By Item" view can show every item mixed
    // together (category-only filter, or no specific item chosen yet), so a
    // single shared running balance would leak one item's closing stock into
    // an unrelated item's opening stock for the next row in date order.
    const previousClosingStockByItem = {};

    const normalizedRows = sortedRows.map((row) => {
      const itemKey = getTextValue(row.name) || "__unknown__";
      const stockIn = getNumericValue(row.qty, row.receipt_qty, row.in_qty, 0) ?? 0;
      const stockOut = getNumericValue(row.sold_qty, row.out_qty, row.issue_qty, 0) ?? 0;
      const rawOpeningStock = getNumericValue(row.ostock, row.opening_stock);
      const openingStock = previousClosingStockByItem[itemKey] ?? rawOpeningStock ?? 0;
      const closingStock = getNumericValue(
        row.total,
        row.closing_stock,
        row.balance,
        openingStock + stockIn - stockOut
      ) ?? 0;

      previousClosingStockByItem[itemKey] = closingStock;

      return {
        ...row,
        qty: stockIn,
        sold_qty: stockOut,
        ref: getTextValue(row.ref, row.reference, row.inv),
        derived_ostock: openingStock,
        derived_closing_stock: closingStock
      };
    });

    return normalizedRows.sort((left, right) => {
      const leftDate = new Date(left.date || 0).getTime();
      const rightDate = new Date(right.date || 0).getTime();

      if (leftDate !== rightDate) {
        return rightDate - leftDate;
      }

      const leftPo = Number(left.po) || 0;
      const rightPo = Number(right.po) || 0;

      if (leftPo !== rightPo) {
        return rightPo - leftPo;
      }

      return (Number(right.id) || 0) - (Number(left.id) || 0);
    });
  }, [filterType, reportData]);

  // Per-item movement summary across every loaded row in the selected date range -
  // the raw material for both the "no stock-out" and "no stock-in" filters below
  const itemMovementSummary = useMemo(() => {
    const grouped = {};

    reportData.forEach((row) => {
      const itemName = getTextValue(row.name) || "Unknown Item";
      const stockIn = getNumericValue(row.qty, row.receipt_qty, row.in_qty, 0) ?? 0;
      const stockOut = getNumericValue(row.sold_qty, row.out_qty, row.issue_qty, 0) ?? 0;
      const amount = getNumericValue(row.amount, 0) ?? 0;
      const rate = getNumericValue(row.rate, 0) ?? 0;
      const rowClosingStock = getNumericValue(row.total, row.closing_stock, row.balance);
      const rowRank = [
        row.date ? new Date(row.date).getTime() : 0,
        Number(row.po) || 0,
        Number(row.id) || 0
      ];

      if (!grouped[itemName]) {
        grouped[itemName] = {
          name: itemName,
          category: getTextValue(row.category, row.type) || "-",
          sname: getTextValue(row.sname, row.supplier_name) || "-",
          total_inward: 0,
          total_outward: 0,
          total_amount: 0,
          rate: 0,
          date: row.date || null,
          closing_stock_qty: 0,
          _latestRank: [-Infinity, -Infinity, -Infinity]
        };
      }

      const bucket = grouped[itemName];
      bucket.total_inward += stockIn;
      bucket.total_outward += stockOut;
      bucket.total_amount += amount;
      if (rate) bucket.rate = rate;

      if (row.date && (!bucket.date || new Date(row.date) > new Date(bucket.date))) {
        bucket.date = row.date;
      }

      if (rowClosingStock !== null && isRankNewer(rowRank, bucket._latestRank)) {
        bucket.closing_stock_qty = rowClosingStock;
        bucket._latestRank = rowRank;
      }
    });

    return Object.values(grouped)
      .map(({ _latestRank, ...item }) => item)
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [reportData]);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Load filter options on mount
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        setLoading(true);

        // Fetch stock data directly from wstock table
        await fetchData("wstock", (stockData) => {
          // Extract unique categories
          const uniqueCategories = [...new Set(stockData.map((item) => item.category || item.type).filter(Boolean))]
            .sort()
            .map((cat) => ({ value: cat, label: cat }));

          // Extract unique suppliers
          const uniqueSuppliers = [...new Set(stockData.map((item) => item.sname).filter(Boolean))]
            .sort()
            .map((sup) => ({ value: sup, label: sup }));

          // Extract unique sponsors
          const uniqueSponsors = [...new Set(stockData.map((item) => item.sponsor_name).filter(Boolean))]
            .sort()
            .map((spon) => ({ value: spon, label: spon }));

          // Extract unique references
          const uniqueReferences = [...new Set(stockData.map((item) => item.ref).filter(Boolean))].sort();

          // Get date range
          const dates = stockData.map((item) => item.date).filter(Boolean).sort();
          const nextDateRange = {
            earliest_date: dates[0] || null,
            latest_date: dates[dates.length - 1] || null
          };

          // Fetch items from items table
          fetchData(
            "items",
            (itemsData) => {
            // Deduplicate by item name
            const uniqueByName = new Map();
            itemsData.forEach((item) => {
              const key = (item.name || "").trim();
              if (key && !uniqueByName.has(key)) {
                uniqueByName.set(key, { name: item.name, label: item.name });
              }
            });
            const items = Array.from(uniqueByName.values()).sort((a, b) => a.label.localeCompare(b.label));

            setFilterOptions({
              categories: uniqueCategories,
              items: items,
              suppliers: uniqueSuppliers,
              sponsors: uniqueSponsors,
              references: uniqueReferences,
              date_range: nextDateRange,
              stock_types: [
                { value: "in", label: "Stock In" },
                { value: "out", label: "Stock Out" }
              ]
            });

            setLoading(false);
          },
            "name",
            { flag: 1 }
          );
        },
        "date",
        {}
        );
      } catch (error) {
        toast.error("Failed to load filter options");
        setLoading(false);
      }
    };
    loadFilterOptions();
  }, []);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      page: 1
    }));
  };

  // Handle date range changes
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    if (dates && dates[0] && dates[1]) {
      setFilters((prev) => ({
        ...prev,
        from: dates[0].format("YYYY-MM-DD"),
        to: dates[1].format("YYYY-MM-DD"),
        page: 1
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        from: "",
        to: "",
        page: 1
      }));
    }
  };

  const handleSelectFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value || "",
      page: 1
    }));
  };

  const handleFilterTypeChange = (value) => {
    setFilterType(value);
    setFilters((prev) => ({
      ...prev,
      name: "",
      sname: "",
      catid: "",
      ref: "",
      page: 1
    }));
    setReportData([]);
    if (!ROW_LEVEL_REPORT_TYPES.includes(value)) {
      setShowNonMovingOnly(false);
      setShowNoStockInOnly(false);
    }
  };

  // Load report data
  const loadReport = async () => {
    if (!filters.from || !filters.to) {
      toast.warn("Please select date range");
      return;
    }

    try {
      setLoading(true);
      let response;

      const params = {
        from: filters.from,
        to: filters.to
      };

      // Route selection based on filter type
      if (filterType === "item" && filters.name) {
        // Filter by date and item
        params.item = filters.name;
        response = await axios.get("/stock/filter-by-date-item", {
          ...getHeaders(),
          params
        });
      } else if (filterType === "supplier" && filters.sname) {
        // Filter by date and supplier
        params.supplier = filters.sname;
        response = await axios.get("/stock/filter-by-date-supplier", {
          ...getHeaders(),
          params
        });
      } else if (filterType === "reference") {
        // Group by invoice/reference
        response = await axios.get("/stock/group-by-invoice", {
          ...getHeaders(),
          params 
        });
      } else if (filterType === "groupbyinv") {
        // Group by invoice with date range
        params.groupBy = "inv";
        response = await axios.get("/stock/group-by-invoice", {
          ...getHeaders(),
          params 
        });
      } else if (filterType === "transactions") {
        // Filter by date - all transactions
        response = await axios.get("/stock/filter-by-date", {
          ...getHeaders(),
          params 
        });
      } else if (filterType === "balance") {
        // Group by item for balance view
        response = await axios.get("/stock/filter-group-by-item", {
          ...getHeaders(),
          params 
        });
      } else {
        // Default: Filter by date only
        response = await axios.get("/stock/filter-by-date", {
          ...getHeaders(),
          params 
        });
      }

      if (response.data) {
        // Check if data is directly in response.data or nested in response.data.data
        const rawDataArray = Array.isArray(response.data)
          ? response.data
          : (response.data.data || []);

        // Category is a cross-cutting filter - apply it on top of whichever endpoint
        // was called, for any report type whose rows actually carry a category field
        const dataArray =
          filters.catid && ROW_LEVEL_REPORT_TYPES.includes(filterType)
            ? rawDataArray.filter((row) => row.category === filters.catid)
            : rawDataArray;

        setReportData(dataArray);
        setPagination({
          current_page: response.data.pagination?.current_page || 1,
          limit: response.data.pagination?.limit || dataArray.length || 50,
          total_records: response.data.pagination?.total_records || dataArray.length,
          total_pages: response.data.pagination?.total_pages || 1
        });
        toast.success(response.data.msg || `Loaded ${dataArray.length} records`);
      } else {
        toast.error("Failed to load report");
      }
    } catch (error) {
      toast.error(error.response?.data?.msg || "Error loading report");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const resetFilters = () => {
    setFilters({
      from: "",
      to: "",
      itemid: "",
      name: "",
      catid: "",
      sname: "",
      sponsor_name: "",
      ref: "",
      page: 1,
      limit: 50
    });
    setFilterType("all");
    setDateRange(null);
    setReportData([]);
    setPagination({
      current_page: 1,
      limit: 50,
      total_records: 0,
      total_pages: 0
    });
    setShowNonMovingOnly(false);
    setShowNoStockInOnly(false);
  };

  const getColumns = () => {
    if (filterType === "item") {
      return [
        { key: "po", label: "PO" },
        { key: "date", label: "Date", isDate: true },
        { key: "ref", label: "Ref" },
        { key: "sname", label: "Supplier" },
        { key: "name", label: "Item" },
        { key: "pass", label: "Pass No." },
        { key: "ostock", label: "Opening Stock", isNumeric: true },
        { key: "qty", label: "In", isNumeric: true },
        { key: "sold_qty", label: "Out", isNumeric: true },
        { key: "total", label: "Closing Stock", isNumeric: true },
        { key: "rate", label: "Rate", isCurrency: true },
        { key: "amount", label: "Amount", isCurrency: true }
      ];
    } else if (filterType === "supplier") {
      return [
        { key: "sno", label: "S.No." },
        { key: "inv", label: "Inv. No" },
        { key: "name", label: "Item Name" },
        { key: "qty", label: "Inward", isNumeric: true },
        { key: "rate", label: "Unit Price", isCurrency: true },
        { key: "amount", label: "Total Amount", isCurrency: true }
      ];
    } else if (filterType === "transactions") {
      return [
        { key: "date", label: "Date", isDate: true },
        { key: "name", label: "Item" },
        { key: "category", label: "Category" },
        { key: "qty", label: "Qty", isNumeric: true },
        { key: "rate", label: "Rate", isCurrency: true },
        { key: "amount", label: "Amount", isCurrency: true }
      ];
    } else if (filterType === "balance") {
      return [
        { key: "name", label: "Item Name" },
        { key: "opening_balance", label: "Opening", isNumeric: true },
        { key: "period_inward", label: "Inward", isNumeric: true },
        { key: "period_outward", label: "Outward", isNumeric: true },
        { key: "closing_balance", label: "Closing", isNumeric: true }
      ];
    } else if (filterType === "reference") {
      return [
        { key: "reference", label: "Reference" },
        { key: "supplier_name", label: "Supplier" },
        { key: "sponsor_name", label: "Sponsor" },
        { key: "unique_items", label: "Items", isNumeric: true },
        { key: "total_inward", label: "Inward Qty", isNumeric: true },
        { key: "total_outward", label: "Outward Qty", isNumeric: true }
      ];
    } else if (filterType === "groupbyinv") {
      return [
        { key: "sno", label: "S.No." },
        { key: "supplier", label: "Supplier Name" },
        { key: "inv", label: "Inv No." },
        { key: "date", label: "Date", isDate: true },
        { key: "amount", label: "Amount", isCurrency: true }
      ];
    } else if (filterType === "category") {
      return [
        { key: "date", label: "Date", isDate: true },
        { key: "name", label: "Item" },
        { key: "category", label: "Category" },
        { key: "sname", label: "Supplier" },
        { key: "qty", label: "Qty", isNumeric: true },
        { key: "rate", label: "Rate", isCurrency: true },
        { key: "amount", label: "Amount", isCurrency: true }
      ];
    }

    return [
      { key: "date", label: "Date", isDate: true },
      { key: "name", label: "Item" },
      { key: "sname", label: "Supplier" },
      { key: "category", label: "Category" },
      { key: "qty", label: "Qty", isNumeric: true },
      { key: "sold", label: "Sold Qty", isNumeric: true },
      { key: "rate", label: "Rate", isCurrency: true },
      { key: "amount", label: "Amount", isCurrency: true }
    ];
  };

  const isMovementFilterActive =
    (showNonMovingOnly || showNoStockInOnly) && ROW_LEVEL_REPORT_TYPES.includes(filterType);
  const isCombinedMovementFilter = showNonMovingOnly && showNoStockInOnly;

  // When both boxes are checked this is a combined (union) report: an item qualifies
  // if it matches EITHER condition, not only when it matches both at once.
  const movementFilteredItems = itemMovementSummary
    .filter((item) => {
      const matchesNoStockOut = showNonMovingOnly && item.total_outward === 0;
      const matchesNoStockIn = showNoStockInOnly && item.total_inward === 0;
      return matchesNoStockOut || matchesNoStockIn;
    })
    .map((item) => {
      if (!isCombinedMovementFilter) return item;

      const matchesNoStockOut = item.total_outward === 0;
      const matchesNoStockIn = item.total_inward === 0;
      const movementStatus =
        matchesNoStockOut && matchesNoStockIn
          ? "No In & No Out"
          : matchesNoStockOut
          ? "No Stock-Out"
          : "No Stock-In";

      return { ...item, movement_status: movementStatus };
    });
  const columns = isMovementFilterActive
    ? (isCombinedMovementFilter
        ? [...MOVEMENT_FILTER_COLUMNS.slice(0, 5), MOVEMENT_STATUS_COLUMN, ...MOVEMENT_FILTER_COLUMNS.slice(5)]
        : MOVEMENT_FILTER_COLUMNS)
    : getColumns();
  const tableData = isMovementFilterActive ? movementFilteredItems : displayData;
  const movementFilterLabel =
    showNonMovingOnly && showNoStockInOnly
      ? "No Stock-Out or No Stock-In Items (Combined)"
      : showNonMovingOnly
      ? "No Stock-Out Items"
      : showNoStockInOnly
      ? "No Stock-In Items"
      : "";

  const calculateTotals = () => {
    const totals = {};

    columns.forEach(col => {
      if (col.key === "total") {
        // Closing Stock is a running balance, not a flow - summing it across every
        // historical row is meaningless. Show the most recent row's balance instead
        // (rows are already ordered newest-first).
        totals[col.key] = tableData.length > 0 ? parseFloat(getCellValue(tableData[0], col.key)) || 0 : 0;
        return;
      }

      if (["qty", "sold_qty", "rate", "amount", "total_amount", "total_inward", "total_outward",
           "opening_balance", "period_inward", "period_outward", "closing_balance", "closing_stock_qty"].includes(col.key)) {
        totals[col.key] = tableData.reduce((sum, row) => {
          const value = parseFloat(getCellValue(row, col.key)) || 0;
          return sum + value;
        }, 0);
      }
    });

    return totals;
  };

  const formatValue = (value, key) => {
    if (!value && value !== 0) return "-";
    if (key === "date") return new Date(value).toLocaleDateString();
    if (key === "type" || key === "category") {
      return <Tag className="stock-report-tag">{String(value).toUpperCase()}</Tag>;
    }
    if (["rate", "amount", "total_amount", "total_inward_amount", "total_outward_amount"].includes(key)) {
      return <Text className="stock-report-currency">฿{parseFloat(value).toFixed(2)}</Text>;
    }
    if (["qty", "sold_qty", "ostock", "total", "total_inward", "total_outward", "net_balance", "opening_balance", "period_inward", "period_outward", "closing_balance", "unique_items", "closing_stock_qty"].includes(key)) {
      return parseFloat(value).toFixed(2);
    }
    return value;
  };

  const getSortableValue = (row, column) => {
    const value = getCellValue(row, column.key);

    if (column.isCurrency || column.isNumeric) {
      return Number(value || 0);
    }

    if (column.isDate) {
      return dayjs(value).valueOf() || 0;
    }

    return String(value || "").toLowerCase();
  };

  const getCellValue = (row, key) => {
    // Handle supplier name with multiple possible field names
    if (key === "sname") {
      return row.sname || row.supplier_name || row.supplierName || "-";
    }
    if (key === "ostock") {
      // Trust the backend's own per-row value first - it's computed against that
      // exact item's own previous transaction. The chained/derived value is only
      // a fallback for rows that don't carry it.
      return row.ostock ?? row.derived_ostock ?? "-";
    }
    if (key === "total") {
      return row.total ?? row.closing_stock ?? row.derived_closing_stock ?? "-";
    }
    if (key === "qty") {
      return row.qty ?? row.receipt_qty ?? row.in_qty ?? 0;
    }
    if (key === "sold_qty") {
      return row.sold_qty ?? row.out_qty ?? row.issue_qty ?? 0;
    }
    if (key === "pass") {
      return row.pass || row.pass_no || "-";
    }
    if (key === "ref") {
      return row.ref || row.reference || row.inv || "-";
    }
    // Handle invoice number - use only inv column from database
    if (key === "inv") {
      return row.inv || "-";
    }
    return row[key];
  };

  const getExportData = () => {
    return tableData.map((row, index) => {
      const exportRow = { ...row, sno: index + 1 };

      return columns.reduce((accumulator, column) => {
        const rawValue = column.key === "sno" ? index + 1 : getCellValue(exportRow, column.key);

        if (column.isCurrency) {
          accumulator[column.label] = Number(rawValue || 0).toFixed(2);
        } else if (column.isNumeric) {
          accumulator[column.label] = Number(rawValue || 0).toFixed(2);
        } else if (column.isDate) {
          accumulator[column.label] = rawValue ? dayjs(rawValue).format("YYYY-MM-DD") : "-";
        } else {
          accumulator[column.label] = rawValue ?? "-";
        }

        return accumulator;
      }, {});
    });
  };

  const exportTitle = `Stock Report - ${REPORT_TYPE_OPTIONS.find((option) => option.value === filterType)?.label || "All Records"}${isMovementFilterActive ? ` (${movementFilterLabel})` : ""}`;

  const exportFilename = `stock_report_${filterType}_${filters.from || "from"}_${filters.to || "to"}`;

  const exportHeaderInfo = {
    dayName: currentDateTime.toLocaleDateString("en-US", { weekday: "long" }),
    date: currentDateTime.toLocaleDateString("en-GB"),
    time: currentDateTime.toLocaleTimeString("en-US", { hour12: true }),
    fromDate: filters.from || "",
    toDate: filters.to || "",
    supplierName: filterType === "supplier" ? filters.sname || "All Suppliers" : ""
  };

  const addPdfHeader = (doc, title, headerInfo) => {
    let startY = 10;

    if (logo) {
      try {
        doc.addImage(logo, "PNG", 14, 8, 30, 12);
      } catch {
        // Logo failed to render - continue exporting without it
      }
    }

    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.text(title, 50, startY + 4);

    doc.setFontSize(8);
    doc.setFont(undefined, "normal");
    doc.text(`${headerInfo.dayName} | ${headerInfo.date} | ${headerInfo.time}`, 50, startY + 10);

    if (headerInfo.fromDate && headerInfo.toDate) {
      doc.text(`Period: ${headerInfo.fromDate} to ${headerInfo.toDate}`, 50, startY + 15);
      startY += 7;
    }

    return startY + 10;
  };

  const handleExportExcel = () => {
    const formattedData = getExportData();

    if (!formattedData.length) {
      toast.warn("No data available for Excel export");
      return;
    }

    const worksheetData = [
      [exportTitle],
      [`Period: ${filters.from || "-"} to ${filters.to || "-"}`],
      [],
      Object.keys(formattedData[0]),
      ...formattedData.map((row) => Object.values(row))
    ];

    const totals = calculateTotals();
    const exportHeaders = Object.keys(formattedData[0]);
    const amountKey = columns.find((column) => column.key === "amount" || column.key === "total")?.label;

    if (amountKey) {
      const totalRow = Array.from({ length: exportHeaders.length }, () => "");
      const amountColumnIndex = exportHeaders.indexOf(amountKey);

      totalRow[0] = "Total";
      if (amountColumnIndex >= 0) {
        totalRow[amountColumnIndex] = Number(totals.amount || totals.total || 0).toFixed(2);
      }

      worksheetData.push([]);
      worksheetData.push(totalRow);
    }

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Report");
    XLSX.writeFile(workbook, `${exportFilename}.xlsx`);
  };

  const handleExportPdf = () => {
    const formattedData = getExportData();

    if (!formattedData.length) {
      toast.warn("No data available for PDF export");
      return;
    }

    const doc = new jsPDF("landscape");
    const startY = addPdfHeader(doc, exportTitle, exportHeaderInfo);
    const headers = Object.keys(formattedData[0]);
    const body = formattedData.map((row) => headers.map((header) => String(row[header] ?? "-")));
    const totals = calculateTotals();
    // Build one footer cell per column (mirrors the on-screen Table.Summary row) instead
    // of a single blank-padded "amount" cell, so every totalled column shows its own sum.
    const footerRow = columns.map((column, index) => {
      if (index === 0) {
        return { content: "TOTAL", styles: { halign: "left", fontStyle: "bold" } };
      }

      const totalValue = totals[column.key];
      const halign = column.isNumeric || column.isCurrency ? "right" : "left";

      if (typeof totalValue === "undefined") {
        return { content: "-", styles: { halign, fontStyle: "bold" } };
      }

      const content = column.isCurrency
        ? `${Number(totalValue).toFixed(2)}`
        : Number(totalValue).toFixed(2);

      return { content, styles: { halign, fontStyle: "bold" } };
    });

    doc.autoTable({
      startY,
      head: [headers],
      body,
      foot: [footerRow],
      styles: { fontSize: 8, cellPadding: 2.5 },
      headStyles: { fillColor: [34, 34, 34] },
      footStyles: { fillColor: [245, 245, 245], textColor: [0, 0, 0] },
      margin: { left: 14, right: 14, bottom: 20 },
      didDrawPage: (data) => {
        const pageHeight = doc.internal.pageSize.getHeight();
        const pageWidth = doc.internal.pageSize.getWidth();
        const footerLineY = pageHeight - 14;

        doc.setDrawColor(200, 200, 200);
        doc.line(14, footerLineY, pageWidth - 14, footerLineY);

        doc.setFontSize(8);
        doc.setFont(undefined, "italic");
        doc.setTextColor(120);
        doc.text("Developed by Cloudnetsoftwares Co. Ltd.", 14, footerLineY + 5);

        doc.setFont(undefined, "normal");
        doc.text(`Page ${data.pageNumber}`, pageWidth - 14, footerLineY + 5, { align: "right" });
      }
    });

    doc.save(`${exportFilename}.pdf`);
  };

  const formatDateTime = () => {
    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const dayName = days[currentDateTime.getDay()];
    const date = currentDateTime.toLocaleDateString("en-GB");
    const time = currentDateTime.toLocaleTimeString("en-US", { hour12: true });
    return { dayName, date, time };
  };

  const { dayName, date, time } = formatDateTime();

  const totals = calculateTotals();

  const summaryCards = [
    {
      title: isMovementFilterActive ? "Filtered Items" : "Records",
      value: tableData.length,
      formatter: (value) => value
    },
    {
      title: "Inward",
      value: totals.qty || totals.period_inward || totals.total_inward || 0,
      formatter: (value) => Number(value).toFixed(2)
    },
    {
      title: "Outward",
      value: totals.sold_qty || totals.period_outward || totals.total_outward || 0,
      formatter: (value) => Number(value).toFixed(2)
    },
    {
      title: "Amount",
      value: totals.amount || totals.total_amount || 0,
      formatter: (value) => `฿${Number(value).toFixed(2)}`
    }
  ];

  const tableColumns = columns.map((column, index) => ({
    title: column.label,
    dataIndex: column.key,
    key: column.key,
    width: column.key === "name" || column.key === "sname" ? 180 : undefined,
    fixed: index === 0 ? "left" : undefined,
    align: column.isNumeric || column.isCurrency ? "right" : "left",
    sorter: (leftRow, rightRow) => {
      const leftValue = getSortableValue(leftRow, column);
      const rightValue = getSortableValue(rightRow, column);

      if (typeof leftValue === "number" && typeof rightValue === "number") {
        return leftValue - rightValue;
      }

      return String(leftValue).localeCompare(String(rightValue), undefined, {
        numeric: true,
        sensitivity: "base"
      });
    },
    sortDirections: ["ascend", "descend"],
    render: (_, row, rowIndex) => {
      const value = column.key === "sno" ? rowIndex + 1 : getCellValue(row, column.key);
      return formatValue(value, column.key);
    }
  }));

  const hasFiltersApplied = Boolean(filters.from && filters.to);
  const filterSummary = [
    `Range: ${filters.from || "-"} to ${filters.to || "-"}`,
    `Report: ${REPORT_TYPE_OPTIONS.find((option) => option.value === filterType)?.label || "All Records"}`,
    filterType === "item" && filters.name ? `Item: ${filters.name}` : null,
    filterType === "supplier" && filters.sname ? `Supplier: ${filters.sname}` : null,
    filterType === "reference" && filters.ref ? `Reference: ${filters.ref}` : null,
    ROW_LEVEL_REPORT_TYPES.includes(filterType) && filters.catid ? `Category: ${filters.catid}` : null,
    isMovementFilterActive ? movementFilterLabel : null
  ].filter(Boolean);

  return (
    <Layout>
      <Header title="Stock Report" />
      <ToastContainer />
      <div className="stock-report-new">
        <Card className="stock-report-shell" bordered={false}>
          <div className="stock-report-topbar">
            <div>
              <Title level={4} className="stock-report-title">
                Minimal Stock Report
              </Title>
              <Text type="secondary">{dayName}, {date} at {time}</Text>
            </div>
            <Space wrap>
              <Button
                icon={<SearchOutlined />}
                type="primary"
                onClick={loadReport}
                loading={loading}
              >
                Load Report
              </Button>
              <Button icon={<ClearOutlined />} onClick={resetFilters}>
                Reset
              </Button>
              <Button
                icon={<FilePdfOutlined />}
                onClick={handleExportPdf}
                disabled={!tableData.length}
              >
                Export PDF
              </Button>
              <Button
                icon={<FileExcelOutlined />}
                onClick={handleExportExcel}
                disabled={!tableData.length}
              >
                Export Excel
              </Button>
            </Space>
          </div>

          <div className="stock-report-filter-panel">
            <div className="stock-report-filters">
              <div className="stock-report-field">
                <Text className="stock-report-label">Report Type</Text>
                <Select
                  value={filterType}
                  options={REPORT_TYPE_OPTIONS}
                  onChange={handleFilterTypeChange}
                  className="stock-report-control"
                />
              </div>
              <div className="stock-report-field">
                <Text className="stock-report-label">Date Range</Text>
                <RangePicker
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  format="YYYY-MM-DD"
                  className="stock-report-control"
                  placeholder={["Start Date", "End Date"]}
                />
              </div>
              <div className="stock-report-field">
                <Text className="stock-report-label">Movement Filter</Text>
                <Space direction="vertical" size={2}>
                  <Checkbox
                    checked={showNonMovingOnly}
                    disabled={!ROW_LEVEL_REPORT_TYPES.includes(filterType)}
                    onChange={(e) => setShowNonMovingOnly(e.target.checked)}
                  >
                    No Stock Out
                  </Checkbox>
                  <Checkbox
                    checked={showNoStockInOnly}
                    disabled={!ROW_LEVEL_REPORT_TYPES.includes(filterType)}
                    onChange={(e) => setShowNoStockInOnly(e.target.checked)}
                  >
                    No Stock In
                  </Checkbox>
                </Space>
              </div>
              <div className="stock-report-field">
                <Text className="stock-report-label">Category</Text>
                <Select
                  value={filters.catid || undefined}
                  options={filterOptions.categories}
                  onChange={(value) => handleSelectFilterChange("catid", value)}
                  allowClear
                  disabled={!ROW_LEVEL_REPORT_TYPES.includes(filterType)}
                  className="stock-report-control"
                  placeholder="All categories"
                  popupMatchSelectWidth={false}
                />
              </div>
              {filterType === "item" && (
                <div className="stock-report-field">
                  <Text className="stock-report-label">Item</Text>
                  <Select
                    value={filters.name || undefined}
                    options={filterOptions.items.map((item) => ({ value: item.name, label: item.label }))}
                    onChange={(value) => handleSelectFilterChange("name", value)}
                    allowClear
                    showSearch
                    className="stock-report-control"
                    optionFilterProp="label"
                    placeholder="Select item"
                    popupMatchSelectWidth={false}
                  />
                </div>
              )}
              {filterType === "supplier" && (
                <div className="stock-report-field">
                  <Text className="stock-report-label">Supplier</Text>
                  <Select
                    value={filters.sname || undefined}
                    options={filterOptions.suppliers}
                    onChange={(value) => handleSelectFilterChange("sname", value)}
                    allowClear
                    showSearch
                    className="stock-report-control"
                    optionFilterProp="label"
                    placeholder="Select supplier"
                    popupMatchSelectWidth={false}
                  />
                </div>
              )}
              {filterType === "reference" && (
                <div className="stock-report-field">
                  <Text className="stock-report-label">Reference</Text>
                  <Input
                    name="ref"
                    value={filters.ref}
                    onChange={handleFilterChange}
                    className="stock-report-control"
                    placeholder="Search reference"
                  />
                </div>
              )}
            </div>

            {hasFiltersApplied && (
              <Alert
                className="stock-report-alert"
                type="info"
                showIcon
                message={filterSummary.join(" • ")}
              />
            )}
          </div>

          <Row gutter={[16, 16]} className="stock-report-stats">
            {summaryCards.map((card) => (
              <Col xs={12} lg={6} key={card.title}>
                <Card bordered={false} className="stock-report-stat-card">
                  <Statistic title={card.title} value={card.value} formatter={card.formatter} />
                </Card>
              </Col>
            ))}
          </Row>

          <Card bordered={false} className="stock-report-table-card">
            {loading ? (
              <div className="stock-report-loading">
                <Spin size="large" />
              </div>
            ) : !tableData.length ? (
              <Empty
                description={
                  isMovementFilterActive
                    ? `No items match "${movementFilterLabel}" in this date range`
                    : "Select filters and load the report"
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <Table
                rowKey={(record, index) => record.id || record.name || `${record.po || "row"}-${index}`}
                columns={tableColumns}
                dataSource={tableData}
                pagination={
                  !isMovementFilterActive && pagination.total_pages > 1
                    ? {
                        current: pagination.current_page,
                        pageSize: pagination.limit,
                        total: pagination.total_records,
                        onChange: handlePageChange,
                        showSizeChanger: false,
                        showTotal: (total) => `${total} records`
                      }
                    : false
                }
                scroll={{ x: "max-content" }}
                summary={() => (
                  <Table.Summary.Row>
                    {columns.map((column, index) => {
                      const totalValue = totals[column.key];

                      if (index === 0) {
                        return (
                          <Table.Summary.Cell index={index} key={column.key}>
                            <Text strong>Total</Text>
                          </Table.Summary.Cell>
                        );
                      }

                      if (typeof totalValue === "undefined") {
                        return <Table.Summary.Cell index={index} key={column.key}>-</Table.Summary.Cell>;
                      }

                      return (
                        <Table.Summary.Cell index={index} key={column.key} align="right">
                          <Text strong>
                            {column.isCurrency
                              ? `฿${Number(totalValue).toFixed(2)}`
                              : Number(totalValue).toFixed(2)}
                          </Text>
                        </Table.Summary.Cell>
                      );
                    })}
                  </Table.Summary.Row>
                )}
              />
            )}
          </Card>
        </Card>
      </div>
    </Layout>
  );
}
